import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { mapsHref, isAddressLinkable } from "../lib/mapsHref";
import { logSearch, logSearchSelect } from "../lib/uiEvents";
import {
  findDealOwnershipHints,
  type DealOwnershipHint,
} from "../lib/dealOwnershipHints";

interface SearchResult {
  id: string;
  homeowner_name: string;
  property_street: string | null;
  property_city: string | null;
  stage: string;
  phase: string;
  claim_number: string | null;
  homeowner_phone: string | null;
  insurance_company: string | null;
  owner_name: string | null;
}

const PHASE_COLORS: Record<string, string> = {
  drone: "bg-purple-100 text-purple-700",
  leads: "bg-blue-100 text-blue-700",
  claims: "bg-amber-100 text-amber-700",
  reinspections: "bg-orange-100 text-orange-700",
  builds: "bg-emerald-100 text-emerald-700",
  closing: "bg-teal-100 text-teal-700",
  closed: "bg-gray-100 text-gray-500",
};

/**
 * UniversalSearch
 *
 * Desktop (sm and up): inline header input with a dropdown of results.
 *
 * Mobile (< sm, May 11, 2026 redesign): the header element renders a
 * compact search "trigger pill". Tapping it opens a full-width drop-down
 * search panel mounted in a portal directly below the sticky header. The
 * panel has a big, comfortably readable input (text-base) so typing
 * is clearly visible above the iOS keyboard, plus a back/cancel button
 * and a clear-text X. Closes on backdrop tap, route change, Esc, or
 * picking a result.
 */
export default function UniversalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  // Deals that MATCH the query but are hidden from this user by RLS (owned by
  // another RM). Rendered as locked, non-clickable rows so a rep never
  // concludes "doesn't exist" and creates a duplicate. See dealOwnershipHints.
  const [hints, setHints] = useState<DealOwnershipHint[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);             // desktop dropdown
  const [mobileOpen, setMobileOpen] = useState(false); // mobile drop-down drawer
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Monotonic id per search invocation — a slow, older response must not
  // overwrite the results/hints of a newer keystroke's search.
  const searchReqId = useRef(0);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close desktop dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Keyboard shortcut: Cmd+K / Ctrl+K to focus, Esc to close
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") {
        setOpen(false);
        setMobileOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Auto-focus the mobile drawer input when it opens
  useEffect(() => {
    if (mobileOpen) {
      const t = setTimeout(() => mobileInputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [mobileOpen]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  // Debounced search
  const search = useCallback(async (q: string) => {
    const myReq = ++searchReqId.current;
    if (q.length < 2) {
      setResults([]);
      setHints([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Cross-RLS ownership hints run in parallel with the visible search.
    const hintsPromise =
      q.length >= 3 ? findDealOwnershipHints({ query: q }) : Promise.resolve([] as DealOwnershipHint[]);

    const searchTerm = `%${q}%`;
    const { data, error } = await supabase
      .from("deals")
      .select(
        `id, homeowner_name, property_street, property_city, stage, phase,
         claim_number, homeowner_phone, insurance_company,
         team_roster!deals_owner_id_fkey(full_name)`
      )
      .or(
        `homeowner_name.ilike.${searchTerm},property_street.ilike.${searchTerm},claim_number.ilike.${searchTerm},homeowner_phone.ilike.${searchTerm},insurance_company.ilike.${searchTerm}`
      )
      .order("updated_at", { ascending: false })
      .limit(12);

    let rows: SearchResult[] = [];
    if (!error && data) {
      rows = data.map((d: any) => ({
        ...d,
        owner_name: d.team_roster?.full_name || null,
        team_roster: undefined,
      }));
    } else {
      const { data: fallback } = await supabase
        .from("deals")
        .select(
          "id, homeowner_name, property_street, property_city, stage, phase, claim_number, homeowner_phone, insurance_company, owner_id"
        )
        .or(
          `homeowner_name.ilike.${searchTerm},property_street.ilike.${searchTerm},claim_number.ilike.${searchTerm},homeowner_phone.ilike.${searchTerm}`
        )
        .order("updated_at", { ascending: false })
        .limit(12);

      rows = (fallback || []).map((d: any) => ({ ...d, owner_name: null }));
    }

    // A newer keystroke's search superseded this one — drop everything.
    if (myReq !== searchReqId.current) return;
    setResults(rows);

    // Hidden-match hints: keep only deals the visible search did NOT return
    // (for TL/ASM/exec the two sets overlap fully, so this renders nothing).
    const visibleIds = new Set(rows.map((r) => r.id));
    const hintRows = (await hintsPromise).filter((h) => !visibleIds.has(h.id));
    if (myReq !== searchReqId.current) return;
    setHints(hintRows);

    // Telemetry: log searches that find nothing (q>=3) — surfaces what people
    // look for but can't find (nav/search gaps). Fire-and-forget.
    if (rows.length === 0 && hintRows.length === 0 && q.length >= 3) logSearch(q, 0);

    setLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 250);
    return () => clearTimeout(timer);
  }, [query, search]);

  const goToDeal = (id: string) => {
    // Telemetry: which query led to opening which deal (search-success signal).
    if (query) logSearchSelect(query, id);
    setOpen(false);
    setMobileOpen(false);
    setQuery("");
    navigate(`/deal/${id}`, { state: { from: location.pathname + location.search } });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const anyOpen = open || mobileOpen;
    if (!anyOpen || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && selectedIdx >= 0) {
      e.preventDefault();
      goToDeal(results[selectedIdx].id);
    }
  };

  // Shared result-row renderer for desktop dropdown + mobile drawer
  const renderResult = (r: SearchResult, i: number) => (
    <button
      key={r.id}
      onClick={() => goToDeal(r.id)}
      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition border-b border-slate-100 last:border-b-0 ${
        i === selectedIdx ? "bg-bardin-sky" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm text-bardin-ink">
          {r.homeowner_name}
        </span>
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
            PHASE_COLORS[r.phase] || "bg-gray-100 text-gray-500"
          }`}
        >
          {r.phase}
        </span>
      </div>
      <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
        {r.property_street &&
          (isAddressLinkable(r) ? (
            <a
              href={mapsHref(r)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="hover:underline hover:text-slate-700"
            >
              {r.property_street}
              {r.property_city ? `, ${r.property_city}` : ""}
            </a>
          ) : (
            <span>
              {r.property_street}
              {r.property_city ? `, ${r.property_city}` : ""}
            </span>
          ))}
        {r.claim_number && (
          <span className="text-slate-400">#{r.claim_number}</span>
        )}
      </div>
      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
        <span>{r.stage.replace(/_/g, " ")}</span>
        {r.owner_name && <span>• {r.owner_name}</span>}
        {r.insurance_company && <span>• {r.insurance_company}</span>}
      </div>
    </button>
  );

  // Locked hint row — a deal exists but belongs to another RM. Not clickable
  // (RLS blocks the deal page anyway); tells the rep exactly who to contact
  // for split access instead of letting them create a duplicate.
  const renderHint = (h: DealOwnershipHint) => (
    <div
      key={h.id}
      className="w-full text-left px-4 py-3 border-b border-amber-100 last:border-b-0 bg-amber-50/60"
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm text-slate-700">
          🔒 {h.homeowner_name ?? "(no name)"}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-800">
          {(h.stage ?? "").replace(/_/g, " ")}
        </span>
      </div>
      <div className="mt-0.5 text-xs text-slate-500">
        {h.property_street}
        {h.property_city ? `, ${h.property_city}` : ""}
      </div>
      <div className="mt-1 text-[11px] text-amber-800">
        Already in the CRM — owned by <strong>{h.owner_name ?? "another rep"}</strong>
        {h.owner_phone ? (
          <>
            {" "}(
            <a href={`tel:${h.owner_phone}`} className="underline font-semibold">
              {h.owner_phone}
            </a>
            )
          </>
        ) : null}
        . Ask them to add you as the split RM for access — don't create a new deal.
      </div>
    </div>
  );

  return (
    <>
      {/* DESKTOP (sm+): inline input + dropdown */}
      <div ref={wrapperRef} className="relative w-full max-w-xl hidden sm:block">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setSelectedIdx(-1);
            }}
            onFocus={() => query.length >= 2 && setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search deals — name, address, claim #, phone…"
            className="w-full pl-9 pr-16 py-2 text-sm rounded-lg bg-slate-700/60 text-white placeholder-slate-400 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">
            ⌘K
          </span>
        </div>

        {open && query.length >= 2 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 max-h-[420px] overflow-y-auto z-50">
            {loading && (
              <div className="px-4 py-3 text-sm text-slate-400 text-center">
                Searching…
              </div>
            )}
            {!loading && results.length === 0 && hints.length === 0 && (
              <div className="px-4 py-3 text-sm text-slate-400 text-center">
                No deals found for "{query}"
              </div>
            )}
            {!loading && results.map(renderResult)}
            {!loading && hints.map(renderHint)}
          </div>
        )}
      </div>

      {/* MOBILE (<sm): trigger pill in the header that opens a drop-down drawer */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        aria-label="Search deals"
        className="sm:hidden flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700/60 border border-slate-600 text-slate-300 text-sm"
      >
        <svg
          className="w-4 h-4 shrink-0 text-slate-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <span className="truncate text-slate-400 text-left">Search deals…</span>
      </button>

      {/* MOBILE drop-down panel — portal'd to <body> so it sits above the
          Layout header and all other UI at z-60. The page underneath is
          covered by a dark backdrop; the search bar drops down from the
          top with a big readable input. */}
      {mobileOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] sm:hidden flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Search deals"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/55"
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />

            {/* Panel — pinned to the top, drops down from the header */}
            <div
              className="relative bg-bardin-ink shadow-2xl flex flex-col"
              style={{ paddingTop: "env(safe-area-inset-top)" }}
            >
              <div className="px-3 py-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close search"
                  className="p-2 text-slate-300 hover:text-white"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <div className="relative flex-1">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    ref={mobileInputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setSelectedIdx(-1);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Search deals — name, address, claim #, phone…"
                    /* text-base (16px) keeps iOS from zooming on focus */
                    className="w-full pl-9 pr-9 py-3 text-base rounded-lg bg-slate-700/80 text-white placeholder-slate-400 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-bardin-teal"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    enterKeyHint="search"
                  />
                  {query.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setQuery("");
                        mobileInputRef.current?.focus();
                      }}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-300 hover:text-white"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Results — scrollable area with capped height so it never
                  runs under the keyboard. dvh adapts to keyboard-shrunk
                  viewport on modern iOS. */}
              <div className="bg-white overflow-y-auto" style={{ maxHeight: "60dvh" }}>
                {query.length < 2 && (
                  <div className="px-4 py-6 text-sm text-slate-400 text-center">
                    Type at least 2 characters to search by name, address,
                    claim&nbsp;#, phone, or insurance company.
                  </div>
                )}
                {query.length >= 2 && loading && (
                  <div className="px-4 py-6 text-sm text-slate-400 text-center">
                    Searching…
                  </div>
                )}
                {query.length >= 2 && !loading && results.length === 0 && hints.length === 0 && (
                  <div className="px-4 py-6 text-sm text-slate-400 text-center">
                    No deals found for "{query}"
                  </div>
                )}
                {query.length >= 2 && !loading && results.map(renderResult)}
                {query.length >= 2 && !loading && hints.map(renderHint)}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
